import React, { useState } from 'react';
import { Group, Text, Badge, ActionIcon, Box, UnstyledButton, Menu, Tooltip } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import {
  IconChevronDown,
  IconChevronRight,
  IconBuilding,
  IconDots,
  IconPlus,
  IconEdit,
  IconTrash,
} from '@tabler/icons-react';

export interface OrgNode {
  id: number | string;
  name: string;
  level: number;
  // 用户数量
  count: number;
  expanded?: boolean;
  children?: OrgNode[];
  originalData?: any; // 包含activedUserCount等原始数据
  parent?: OrgNode; // 父节点引用
  departmentPath?: string; // 部门完整路径
}

interface OrgTreeNodeProps {
  node: OrgNode;
  level?: number;
  onToggle: (nodeId: number | string) => void;
  onSelect: (nodeId: number | string) => void;
  selectedDeptId: number | string | null;
  searchTerm: string;
  onAddSubDepartment?: (parentNode: OrgNode) => void;
  onEditDepartment?: (node: OrgNode) => void;
  onDeleteDepartment?: (node: OrgNode) => void;
  canCreateDepartment?: (parentNode: OrgNode) => boolean;
  canEditDepartment?: (node: OrgNode) => boolean;
  canDeleteDepartment?: (node: OrgNode) => boolean;
  /** 编辑企业名称（仅根节点菜单显示，不传则不显示） */
  onEditCompany?: () => void;
  activeMenuNodeId: number | string | null;
  onSetActiveMenuNode: (nodeId: number | string | null, withDelay?: boolean) => void;
}

const OrgTreeNode: React.FC<OrgTreeNodeProps> = ({
  node,
  level = 0,
  onToggle,
  onSelect,
  selectedDeptId,
  searchTerm,
  onAddSubDepartment,
  onEditDepartment,
  onDeleteDepartment,
  canCreateDepartment,
  canEditDepartment,
  canDeleteDepartment,
  onEditCompany,
  activeMenuNodeId,
  onSetActiveMenuNode,
}) => {
  const { t } = useTranslation();
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const hasChildren = node.children && node.children.length > 0;

  // 判断当前节点的操作菜单是否应该显示
  const showMenu = activeMenuNodeId === node.id;

  // 构建当前节点的完整部门路径
  const buildDepartmentPath = (): string => {
    const pathParts: string[] = [];
    let current: OrgNode | undefined = node;
    while (current) {
      if (current.id !== 'root') {
        pathParts.unshift(current.name);
      }
      current = current.parent;
    }
    return pathParts.join(' > ');
  };

  // 为节点添加部门路径信息
  const nodeWithPath = {
    ...node,
    departmentPath: buildDepartmentPath(),
  };
  const indent = level * 12; // 每层缩进12px，比原来更紧凑

  // 检查是否匹配搜索条件
  const isHighlighted = searchTerm && node.name.toLowerCase().includes(searchTerm.toLowerCase());

  // 检查子节点是否有匹配的
  const hasMatchingChildren = (node: OrgNode, searchTerm: string): boolean => {
    if (!searchTerm || !node.children) return false;

    return node.children.some((child: OrgNode) => {
      if (child.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
      return hasMatchingChildren(child, searchTerm);
    });
  };

  // 检查父节点是否有匹配的
  const hasMatchingParents = (node: OrgNode, searchTerm: string): boolean => {
    if (!searchTerm || !node.parent) return false;

    let current: OrgNode | undefined = node.parent;
    while (current) {
      if (current.name.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }
      current = current.parent;
    }
    return false;
  };

  const shouldShowInSearch = hasMatchingChildren(node, searchTerm) || hasMatchingParents(node, searchTerm);

  // 如果有搜索词，且当前节点、父节点、子节点都不匹配，则不显示
  if (searchTerm && !isHighlighted && !shouldShowInSearch) {
    return null;
  }

  // 处理鼠标进入事件 - 立即显示
  const handleMouseEnter = () => {
    onSetActiveMenuNode(node.id, false);
  };

  // 处理鼠标离开事件 - 延迟隐藏
  const handleMouseLeave = () => {
    // 如果菜单已打开，则不隐藏操作按钮
    if (isMenuOpened) {
      return;
    }
    // 调用父组件的带延迟隐藏函数
    onSetActiveMenuNode(null, true);
  };

  return (
    <Box>
      <Box
        style={{
          position: 'relative',
          width: '100%',
          minHeight: '36px', // 保证最小高度
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <UnstyledButton
          onClick={() => {
            if (hasChildren) {
              onToggle(node.id);
            }
            onSelect(node.id);
          }}
          style={{
            width: '100%',
            display: 'block',
          }}
        >
          <Box
            p="xs"
            style={{
              marginLeft: `${indent}px`,
              // 为右侧操作按钮预留 40px 空间
              marginRight: '40px',
              borderRadius: '4px',
              backgroundColor: isHighlighted ? '#fef3c7' : selectedDeptId === node.id ? '#f0f9ff' : 'transparent',
              border: isHighlighted
                ? '1px solid #fbbf24'
                : selectedDeptId === node.id
                ? '1px solid #3b82f6'
                : '1px solid transparent',
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative',
            }}
            onMouseEnter={(e) => {
              if (selectedDeptId !== node.id && !isHighlighted) {
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }
            }}
            onMouseLeave={(e) => {
              if (selectedDeptId !== node.id && !isHighlighted) {
                e.currentTarget.style.backgroundColor = 'transparent';
              }
            }}
          >
            <Group gap="xs" align="flex-start" wrap="nowrap">
              {hasChildren ? (
                node.expanded || shouldShowInSearch ? (
                  <IconChevronDown size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                ) : (
                  <IconChevronRight size={16} style={{ marginTop: '2px', flexShrink: 0 }} />
                )
              ) : (
                <Box w={16} style={{ flexShrink: 0, marginTop: '2px' }} />
              )}

              <IconBuilding size={16} color="#6b7280" style={{ marginTop: '2px', flexShrink: 0 }} />

              <Box style={{ flex: 1, minWidth: 0 }}>
                <Tooltip
                  label={node.departmentPath || node.name}
                  position="top-start"
                  multiline
                  className="max-w-[300px]"
                >
                  <Text
                    size="sm"
                    fw={isHighlighted ? 500 : 400}
                    style={{
                      wordBreak: 'break-word',
                      lineHeight: '1.4',
                    }}
                    truncate="end"
                  >
                    {node.name}
                  </Text>
                </Tooltip>
              </Box>

              <Badge
                size="sm"
                variant="light"
                color="gray"
                style={{
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                  marginTop: '1px',
                }}
              >
                {node.count}
              </Badge>
            </Group>
          </Box>
        </UnstyledButton>

        {/* 操作按钮 - 固定在右上角 */}
        {(showMenu || isMenuOpened) &&
          (onAddSubDepartment || onEditDepartment || onDeleteDepartment || onEditCompany) &&
          // 检查是否有任何操作权限
          ((node.id === 'root' && !!onEditCompany) ||
          !canCreateDepartment ||
          canCreateDepartment(node) ||
          !canEditDepartment ||
          (node.id !== 'root' && canEditDepartment(node)) ||
          !canDeleteDepartment ||
          (!hasChildren &&
            (node.originalData?.activedUserCount === 0 || node.count === 0) &&
            canDeleteDepartment(node)) ? (
            <Box
              style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                zIndex: 10,
              }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <Menu
                position="right-start"
                withArrow
                opened={isMenuOpened}
                onOpen={() => {
                  // 菜单打开时立即显示，取消任何隐藏操作
                  onSetActiveMenuNode(node.id, false);
                  setIsMenuOpened(true);
                }}
                onClose={() => {
                  setIsMenuOpened(false);
                  // 菜单关闭后延迟隐藏操作按钮
                  onSetActiveMenuNode(null, true);
                }}
              >
                <Menu.Target>
                  <ActionIcon
                    variant="subtle"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.9)',
                      border: '1px solid #e5e7eb',
                      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
                    }}
                  >
                    <IconDots size={16} />
                  </ActionIcon>
                </Menu.Target>
                <Menu.Dropdown>
                  {/* 编辑企业名称 - 仅根节点，权限由父组件决定是否传入 onEditCompany */}
                  {onEditCompany && node.id === 'root' && (
                    <Menu.Item
                      leftSection={<IconEdit size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onEditCompany();
                      }}
                    >
                      {t('adminConsole.newConsole.editCompany')}
                    </Menu.Item>
                  )}
                  {onAddSubDepartment && (!canCreateDepartment || canCreateDepartment(nodeWithPath)) && (
                    <Menu.Item
                      leftSection={<IconPlus size={14} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddSubDepartment(node);
                      }}
                    >
                      {t('adminConsole.newConsole.menu.addSubDept')}
                    </Menu.Item>
                  )}
                  {onEditDepartment &&
                    node.id !== 'root' &&
                    (!canEditDepartment || canEditDepartment(nodeWithPath)) && (
                      <Menu.Item
                        leftSection={<IconEdit size={14} />}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEditDepartment(node);
                        }}
                      >
                        {t('adminConsole.newConsole.menu.editDept')}
                      </Menu.Item>
                    )}
                  {/* 删除部门 - 仅叶子节点且没有用户时显示，并检查权限 */}
                  {onDeleteDepartment &&
                    node.id !== 'root' &&
                    (!canDeleteDepartment || canDeleteDepartment(nodeWithPath)) && (
                      <Menu.Item
                        leftSection={<IconTrash size={14} />}
                        color="red"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDeleteDepartment(node);
                        }}
                      >
                        {t('adminConsole.newConsole.menu.deleteDept')}
                      </Menu.Item>
                    )}
                </Menu.Dropdown>
              </Menu>
            </Box>
          ) : null)}
      </Box>
      {hasChildren && node.expanded && (
        <Box>
          {node.children!.map((child: OrgNode) => {
            // 为子节点设置父节点引用
            const childWithParent = { ...child, parent: node };
            return (
              <OrgTreeNode
                key={child.id}
                node={childWithParent}
                level={level + 1}
                onToggle={onToggle}
                onSelect={onSelect}
                selectedDeptId={selectedDeptId}
                searchTerm={searchTerm}
                onAddSubDepartment={onAddSubDepartment}
                onEditDepartment={onEditDepartment}
                onDeleteDepartment={onDeleteDepartment}
                canCreateDepartment={canCreateDepartment}
                canEditDepartment={canEditDepartment}
                canDeleteDepartment={canDeleteDepartment}
                onEditCompany={onEditCompany}
                activeMenuNodeId={activeMenuNodeId}
                onSetActiveMenuNode={onSetActiveMenuNode}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

interface OrganizationTreeProps {
  data: OrgNode[];
  selectedDeptId: number | string | null;
  searchTerm: string;
  onToggle: (nodeId: number | string) => void;
  onSelect: (nodeId: number | string) => void;
  onAddSubDepartment?: (parentNode: OrgNode) => void;
  onEditDepartment?: (node: OrgNode) => void;
  onDeleteDepartment?: (node: OrgNode) => void;
  canCreateDepartment?: (parentNode: OrgNode) => boolean;
  canEditDepartment?: (node: OrgNode) => boolean;
  canDeleteDepartment?: (node: OrgNode) => boolean;
  /** 编辑企业名称（仅根节点菜单显示，不传则不显示） */
  onEditCompany?: () => void;
}

export const OrganizationTree: React.FC<OrganizationTreeProps> = ({
  data,
  selectedDeptId,
  searchTerm,
  onToggle,
  onSelect,
  onAddSubDepartment,
  onEditDepartment,
  onDeleteDepartment,
  canCreateDepartment,
  canEditDepartment,
  canDeleteDepartment,
  onEditCompany,
}) => {
  // 统一管理当前激活的菜单节点 ID
  const [activeMenuNodeId, setActiveMenuNodeId] = useState<number | string | null>(null);
  // 在父组件层面管理隐藏定时器，确保全局只有一个定时器
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

  // 智能设置激活节点：立即显示，带延迟隐藏
  const handleSetActiveMenuNode = React.useCallback((nodeId: number | string | null, withDelay = false) => {
    // 清除任何待执行的定时器
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    if (withDelay && nodeId === null) {
      // 延迟隐藏
      hideTimeoutRef.current = setTimeout(() => {
        setActiveMenuNodeId(null);
        hideTimeoutRef.current = null;
      }, 200);
    } else {
      // 立即设置（显示或隐藏）
      setActiveMenuNodeId(nodeId);
    }
  }, []);

  // 清理定时器
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box>
      {data.map((node) => (
        <OrgTreeNode
          key={node.id}
          node={node}
          onToggle={onToggle}
          onSelect={onSelect}
          selectedDeptId={selectedDeptId}
          searchTerm={searchTerm}
          onAddSubDepartment={onAddSubDepartment}
          onEditDepartment={onEditDepartment}
          onDeleteDepartment={onDeleteDepartment}
          canCreateDepartment={canCreateDepartment}
          canEditDepartment={canEditDepartment}
          canDeleteDepartment={canDeleteDepartment}
          onEditCompany={onEditCompany}
          activeMenuNodeId={activeMenuNodeId}
          onSetActiveMenuNode={handleSetActiveMenuNode}
        />
      ))}
    </Box>
  );
};
